import { queryOptions } from '@tanstack/react-query';

import {
  apiGet,
  apiPatch,
  apiPost,
  type ApiError,
  type BodyOf,
  type QueryOf,
  type ResponseOf,
} from './http';
import { queryKeys } from './queryKeys';

/**
 * **The tenant's question sets, and the questions inside them** (§6.9) —
 * `GET/POST /api/v1/question-sets/`, `GET/PATCH /api/v1/question-sets/{id}/`, the two set actions, and
 * the five routes that write a **question**, every one of them addressed through the set that owns it.
 *
 * ⚠️ **`api/questions.ts` is deleted and its contents are here, because the collection it was named
 * after no longer exists** (ADR 0019, 2026-09-24). A question belongs to exactly one set; two sets
 * holding the same wording hold **two questions**, and editing one does not change the other. So
 * `GET /api/v1/questions/` is gone from the document — **removed, not deprecated**, on ADR 0018's own
 * precedent with the seven role-scoped question routes — and every remaining question route is
 * `…/question-sets/{set_id}/questions/…`. A module whose name asserted a tenant-wide list of questions
 * would be the client's copy of the claim this sprint retracts. That module's own header said *"a set
 * is not a question and is not nested under one … the two modules share no path segment"*; the second
 * half stopped being true the moment questions moved under the set, which is what merges them.
 *
 * ⚠️ **The routes that attach a set to a vacancy are deliberately *not* here.**
 * `/api/v1/roles/{role_id}/question-sets/` and its `detach/` are the **vacancy's** — the path is the
 * vacancy's, and the access rule is the catalogue's (`Admin`, the same `write_access` as `post ROLES`
 * and `patch ROLE`) rather than this surface's per-row authorship. They live in `api/vacancies.ts` with
 * the rest of the vacancy's six.
 *
 * Five things are worth knowing before adding to it:
 *
 *  * **There is no `DELETE` on any path here, and there is no helper for one.** A set is *retired*
 *    (`retireQuestionSet`) and a question is *removed* — which **archives** it (`removeQuestion`, ADR
 *    0005, feedback protection #2). The row, its wording, its set, its position and every `Score` that
 *    quotes it all stay. ⚠️ **Removing and archiving are one act with one route**, because with one set
 *    per question every removal is a removal from the question's only set (ADR 0019 §5, superseding ADR
 *    0018 decision 5): `POST …/questions/{id}/remove/` archives, `…/restore/` undoes it, and there is
 *    no second route and no second name. Two helpers doing one thing would be the client's version of
 *    the screen lying about itself.
 *  * **`is_active` is not writable through `PATCH`** on either noun. Archiving and retiring are actions
 *    (ADR 0005, the shape of ADR 0010); `QuestionSetUpdate` carries `name` alone and `QuestionUpdate`
 *    carries the three wording fields, so no screen can write either flag by mistake. All four actions
 *    take **no body at all**.
 *  * **The reorder takes the full permutation** of the set's question ids, **archived ones included**,
 *    bounded at `MAX_SET_QUESTION_IDS` — checked against the set's own composition as an **equality**,
 *    so a body built from a stale read is a 422 rather than a silent reshuffle, and two reorders
 *    serialise under the set's row lock with the loser refused rather than merged.
 *  * ⚠️ **Which is why `questionSetQuery` always asks with `include_archived=true`** — see its own note.
 *    A composition read at the route's default (`false`) cannot produce a legal reorder body.
 *  * **`author_name` is a *name*, uniform for every reader** (ADR 0016 decision 5(a), 2026-08-12),
 *    resolved through `name_without_an_address` and never `display_name` — whose chain ends at
 *    `username`, which **is** the email address for every account `POST /api/v1/users/` creates. A
 *    `null` means **nobody wrote this set**, which is every set the ADR 0018 migration created, and it
 *    is a different case from an account with no name of its own (that reads *"Unnamed account"*).
 *    ⚠️ **A question carries no author at all any more** — see `Question` below.
 *
 * ⚠️ **Read is admitted to all three groups at the door and the *rows* are narrowed per caller** —
 * two different things, both true (ADR 0018's 2026-08-12 reach entry, which ADR 0019 restates rather
 * than re-decides). An `Admin` or a `Recruiter` reads the tenant's sets; anybody else reads the sets
 * they authored plus the sets attached to a vacancy their own assignments touch. That is why every key
 * in this family carries the **viewer**: this route is *narrowed* rather than *refused*, which is
 * precisely the distinction `queryKeys.roleOptions` was given a viewer for. ⚠️ **Since ADR 0019 that
 * predicate governs questions too** — a question is reachable only *through* its set, so the set's
 * reach **is** the question's reach, and there is no second definition of it anywhere.
 *
 * **Write is the author or an `Admin`**, per row rather than per group, and an author-less set is an
 * `Admin`'s alone — asked where the row is (`pages/QuestionBank/setAccess.ts`) and never here.
 * ⚠️ **There is one write gate and it is the set's** (ADR 0019, ADR 0016 decision 5(c)): writing a
 * question is writing the set that owns it, so `mayWriteQuestion` is gone rather than re-pointed.
 *
 * Nothing here catches an `ApiError`. A 401 signs the app out (`queryClient.ts`) and a 429 reads as a
 * throttle — both decided once, in `http.ts` and `ApiErrorState`.
 */

/** `?include_retired=`, `?author_id=`, `?page=`, `?page_size=`. */
export type QuestionSetListQuery = NonNullable<QueryOf<'/api/v1/question-sets/', 'get'>>;

/** `PagedQuestionSetOut` — the rows and the `count` the list's pager and its range line read. */
export type QuestionSetPage = ResponseOf<'/api/v1/question-sets/', 'get'>;

/**
 * `QuestionSetOut` — `id`, `name`, `is_active`, `author_id`, `author_name` and
 * `active_question_count`.
 *
 * ⚠️ **It carries no vacancy title, and that is the reach decision rather than an omission** (ADR
 * 0018, 2026-08-12). The migration named every set after its vacancy, so a row that helpfully said
 * *"offered by: Senior Backend Engineer"* would hand over through a second field the catalogue the
 * first one is being narrowed to protect.
 */
export type QuestionSet = QuestionSetPage['items'][number];

/**
 * `QuestionSetDetailOut` — the row's own keys plus `questions`, in `Question.position` order.
 *
 * ⚠️ **The key was `items` and held a `QuestionSetItemOut` until 2026-09-24** (ADR 0019). Both the name
 * and the shape named the link table: an *item* was a question's place in a set, carrying a
 * `question_id` alias and a `position` off the link. A set owns its questions now, so the list is of
 * **questions**, and the `id` on each row **is** the question's — the id every route here addresses.
 */
export type QuestionSetDetail = ResponseOf<'/api/v1/question-sets/{set_id}/', 'get'>;

/**
 * `QuestionOut` — `id`, `text`, `category`, `difficulty`, `is_active` and `position`. One row of a
 * set's composition, and the only shape a question ever has on the wire.
 *
 * ⚠️ **The wording here is the question's *live* text, and that is the right value on this surface and
 * the wrong one on an interview.** A set is catalogue; ADR 0004's frozen `question_text_snapshot` is
 * feedback, and the two never share a shape.
 *
 * ⚠️ **`position` is back and `author_id` is gone, and the second is a decision rather than an
 * omission** (ADR 0019, 2026-09-24). The order is the question's own again — it is what
 * `create_interview` freezes into `Score.position` — and an **archived** question keeps it, which is
 * exactly why *Restore* puts a row back where it sat instead of at the end. The author does **not**
 * travel: ADR 0016 decision 5(b) admitted an id *on an enumerable catalogue list*, there is no such
 * list of questions any more, and the write gate is the **set's** — so a question's author decides
 * nothing anybody can act on, and BUG-048's standing rule (no field without a caller) takes it off the
 * payload. `Question.author` the **column** stays on the backend; keeping it unpublished is what makes
 * it a record rather than a disclosure. **Do not reintroduce it here.**
 */
export type Question = QuestionSetDetail['questions'][number];

/**
 * `QuestionSetIn` — a name, and nothing else. **No `author`** (the session user is the author, and an
 * author a client could assert would be an author the write gate could not trust), no `is_active` (a
 * new set is active; retiring is the action) and no questions (composing is a separate act).
 */
export type NewQuestionSet = BodyOf<'/api/v1/question-sets/', 'post'>;

/** `QuestionSetUpdate` — the name, **omittable rather than nullable** (an explicit `null` is a 422). */
export type QuestionSetEdit = BodyOf<'/api/v1/question-sets/{set_id}/', 'patch'>;

/**
 * `QuestionIn` — text, category, difficulty. **No `author`**, no `is_active`, no `position` (a new
 * question is appended last and choosing where it lands is the reorder's job) and — the one this
 * sprint adds — **no `set_id`**: the set is the *address* of the route, and a set in the body would be
 * a second place to say which set is being written, one of them unguarded by the write gate.
 */
export type NewQuestion = BodyOf<'/api/v1/question-sets/{set_id}/questions/', 'post'>;

/**
 * `QuestionUpdate` — text, category, difficulty, each **omittable rather than nullable** (an explicit
 * `null` is a 422). `is_active`, `position`, `author` and `tenant` are absent from the schema on
 * purpose.
 */
export type QuestionEdit = BodyOf<
  '/api/v1/question-sets/{set_id}/questions/{question_id}/',
  'patch'
>;

/** `QuestionSetReorder` — `question_ids`, the **full permutation**; see this module's header. */
export type QuestionSetOrder = BodyOf<'/api/v1/question-sets/{set_id}/reorder/', 'post'>;

/**
 * The sets this caller may read, one page at a time.
 *
 * ⚠️ **The key carries the viewer, and on this route that is not a precaution** — see the module
 * header. Two readers of the same URL legitimately receive different **rows**, so one entry shared
 * between them is one caller's list served to another.
 */
export function questionSetsQuery(viewerId: number, query?: QuestionSetListQuery) {
  return queryOptions<QuestionSetPage, ApiError>({
    queryKey: queryKeys.questionSets(viewerId, query),
    queryFn: () => apiGet('/api/v1/question-sets/', { query }),
  });
}

/**
 * One set with its composition, in `position` order — what §6.9's set screen renders.
 *
 * ⚠️ **`include_archived=true`, always, and this is the one parameter on this surface that is not the
 * screen's to choose** (ADR 0019, 2026-09-24). Two independent reasons, and the first alone decides it:
 *
 *  1. **The reorder body is a permutation over the whole composition, archived rows included**, checked
 *     by the server as an equality. The route's own default is `include_archived=false`, so an order
 *     built from a default read is **missing rows and is a 422** — a screen that can reorder must hold
 *     the whole composition. Asking for it conditionally would make the reorder legal or illegal
 *     depending on a toggle nobody connects to it.
 *  2. ***Restore* has to be able to find its target.** A question that vanished the instant it was
 *     removed would leave a state with no way back into it — the defect TASK-114 shipped on the staff
 *     surface and TASK-115 had to come back for.
 *
 * So *Show archived* is a filter over the composition this client already holds, exactly as the
 * library's toggle was, and it costs no request and forks no second cache entry. That is also why the
 * key below carries no query: there is only ever one read of a set.
 *
 * A 404 means the set is outside this caller's **reach** as much as it means the set never existed,
 * and the two are indistinguishable by design (404 before 403). Nothing here maps it to §8's Forbidden
 * state — that mapping belongs to the candidate surface alone (`CANDIDATE_SCOPED_RECORD`), and *"You
 * do not have access to this candidate."* over a set would be a sentence about somebody nobody named.
 */
export function questionSetQuery(viewerId: number, setId: number) {
  return queryOptions<QuestionSetDetail, ApiError>({
    queryKey: queryKeys.questionSet(viewerId, setId),
    queryFn: () =>
      apiGet('/api/v1/question-sets/{set_id}/', {
        params: { set_id: setId },
        query: { include_archived: true },
      }),
  });
}

/**
 * **Any of the three groups**, and the caller is the author — ADR 0016 decision 5(c): an
 * `Interviewer` *curates* and does not *assign*, so they may build sets of their own and may not
 * attach one to a vacancy or schedule with it.
 */
export const createQuestionSet = (set: NewQuestionSet) => apiPost('/api/v1/question-sets/', set);

/** **The author or an `Admin`.** Renaming is the whole of `PATCH` on a set. */
export const renameQuestionSet = (setId: number, changes: QuestionSetEdit) =>
  apiPatch('/api/v1/question-sets/{set_id}/', changes, { params: { set_id: setId } });

/**
 * Retirement, and **the reason there is no delete** (ADR 0005, feedback protection #2): the row stays
 * in the workspace, every interview ever built from it keeps its frozen questions, and it is simply
 * not offered when an interview is scheduled. Idempotent on the server, and **bodiless** — `is_active`
 * is not a field anyone sends.
 */
export const retireQuestionSet = (setId: number) =>
  apiPost('/api/v1/question-sets/{set_id}/retire/', undefined, { params: { set_id: setId } });

/** The other half, equally idempotent and equally bodiless. */
export const restoreQuestionSet = (setId: number) =>
  apiPost('/api/v1/question-sets/{set_id}/restore/', undefined, { params: { set_id: setId } });

/**
 * **Write a question into this set** — the act the whole sprint exists for, and the one the user asked
 * for in as many words: *"there should be only question sets, and adding, removing and so on should be
 * done inside each set"* — translated, because this repository is written in English throughout and
 * ADR 0019 quotes the original.
 *
 * ⚠️ **The body is a wording, not an id.** It took an existing question's id until 2026-09-24 —
 * *"pick from the library"*, a wording retracted with the entity — and there is no library to pick
 * from: two sets given the same wording
 * hold **two questions**, and editing one does not change the other. The new row is appended **last**
 * and is active.
 *
 * The write gate is the **set's** — author-or-`Admin` — because composing a set is writing the set and
 * not the questions in it. There is no per-question gate to ask.
 */
export const createQuestion = (setId: number, question: NewQuestion) =>
  apiPost('/api/v1/question-sets/{set_id}/questions/', question, { params: { set_id: setId } });

/**
 * **The set's author or an `Admin`.** Editing the wording does **not** rewrite history: `Score` keeps
 * the question text as it stood when the interview was arranged (ADR 0004, feedback protection #1),
 * which is the sentence the edit dialog owes the person doing the edit.
 *
 * ⚠️ **And it reaches exactly one set.** A colleague's set holding the same wording holds a different
 * row with a different id; this `PATCH` cannot touch it, which is the whole of ADR 0019's second half.
 */
export const updateQuestion = (setId: number, questionId: number, changes: QuestionEdit) =>
  apiPatch('/api/v1/question-sets/{set_id}/questions/{question_id}/', changes, {
    params: { set_id: setId, question_id: questionId },
  });

/**
 * **Remove a question from this set — which archives it**, and that sentence is one act rather than
 * two (ADR 0019 §5, superseding ADR 0018 decision 5; ADR 0005, feedback protection #2).
 *
 * With one set per question there is no link to drop: every removal is a removal from the question's
 * only set, so *"remove from this set"* and *"archive"* name the same thing and the API has **one**
 * route for it. The row keeps its id, its wording, its category, its difficulty, **its set and its
 * `position`** — which is why *Restore* puts it back where it sat — and every `Score` keeps its
 * foreign key and its frozen snapshot. Interviews already arranged still ask it; what changes is what
 * `create_interview` materialises from now on.
 *
 * ⚠️ **A `POST …/remove/` and never a `DELETE`.** There is no `DELETE` operation on any path of this
 * surface for a client to call: deleting a question would take the recorded feedback that quotes it,
 * which is what `Score.question`'s `PROTECT` refuses at the database and what ADR 0005 refuses in the
 * product's own terms. Idempotent, and **bodiless**.
 */
export const removeQuestion = (setId: number, questionId: number) =>
  apiPost('/api/v1/question-sets/{set_id}/questions/{question_id}/remove/', undefined, {
    params: { set_id: setId, question_id: questionId },
  });

/** The other half, equally idempotent and equally bodiless: the row is active again, in its place. */
export const restoreQuestion = (setId: number, questionId: number) =>
  apiPost('/api/v1/question-sets/{set_id}/questions/{question_id}/restore/', undefined, {
    params: { set_id: setId, question_id: questionId },
  });

/**
 * The order the freeze reads (`Question.position`, ADR 0004, feedback protection #1) — **the full
 * permutation of this set's question ids, archived ones included**.
 *
 * ⚠️ **Reordering changes the order of interviews arranged afterwards and changes no interview that
 * already exists**, whose scores and their positions were materialised when it was arranged. The
 * server checks the body against the set's own composition as an equality, so a permutation built from
 * a list it has already superseded is a **422** and never a silent reshuffle — which is why the one
 * caller of this (§6.9's set screen) serialises its moves rather than firing them as they are pressed,
 * and why `questionSetQuery` reads with `include_archived=true` rather than leaving the completeness
 * of the body to a toggle.
 */
export const reorderQuestionSet = (setId: number, order: QuestionSetOrder) =>
  apiPost('/api/v1/question-sets/{set_id}/reorder/', order, { params: { set_id: setId } });
